import { ChangeDetectionStrategy, Component, OnInit, inject, input, viewChild } from '@angular/core';
import { map, of, take } from 'rxjs';
import { CmgUiUtilities } from '../../../cmg-1003/components/common/cmg-ui-utils';
import { nonNullish } from '../../utils';
import { EmploymentModel, IncomeModel } from '../../../cmg-1003/models/employment-income-expense.model';
import { EmploymentStatus, employmentIncomeTypes, nonEmploymentIncomeTypes } from '../../../cmg-1003/models/lookups/employment.lookups';
import { IncomeFrequencyType } from '../../../cmg-1003/components/employment-income-expenses/income/income-calculator/income.lookups';
import {
  Cmg1003ApplicationInfoQuery,
  Cmg1003AssetsQuery,
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003EmploymentIncomeExpensesStore,
  Cmg1003StoreQueryService,
} from '../../../loan-state';
import { LoanBannerService } from '../../../loan-banner/services/loan-banner.service';
import { BorrowerModel } from '../../../cmg-1003/models';
import { Table, TableModule } from 'primeng/table';
import { ConfirmationModalCloseType } from '../../../models';
import { ConfirmationModalComponent } from '../../../core';
import { DialogService } from 'primeng/dynamicdialog';
import { Accordion } from 'primeng/accordion';
import { Panel } from 'primeng/panel';
import { PrimeTemplate } from 'primeng/api';
import { NgClass, AsyncPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ButtonDirective, Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { InputNumber } from 'primeng/inputnumber';
import { CmgPrimeNgInputNumberFocusDirective } from '../../directives/cmg-input-number-focus.directive';
import { Drawer } from 'primeng/drawer';
import { AddEditEmploymentIncomeComponent } from './add-edit-employment-income/add-edit-employment-income.component';
import { ToNameOrValuePipe } from '../../pipes/to-name.pipe';

/**
 * @deprecated
 * This component has been replaced a new version and can be removed when the EmploymentTableFlyoutRedesign feature flag is removed
 */
@Component({
  selector: 'employment-income-expenses',
  templateUrl: './employment-income-expenses.component.html',
  styleUrls: ['./employment-income-expenses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Accordion,
    Panel,
    PrimeTemplate,
    TableModule,
    NgClass,
    ButtonDirective,
    Button,
    Select,
    ReactiveFormsModule,
    FormsModule,
    InputNumber,
    CmgPrimeNgInputNumberFocusDirective,
    Drawer,
    AddEditEmploymentIncomeComponent,
    AsyncPipe,
    CurrencyPipe,
    DatePipe,
    ToNameOrValuePipe,
  ],
})
export class EmploymentIncomeExpensesComponent implements OnInit {
  private readonly _empIncExpQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _appQuery = inject(Cmg1003ApplicationInfoQuery);
  private readonly _empIncExpStore = inject(Cmg1003EmploymentIncomeExpensesStore);
  private readonly _assetsQuery = inject(Cmg1003AssetsQuery);
  private readonly _storeQuery = inject(Cmg1003StoreQueryService);
  private readonly _dialogService = inject(DialogService);
  private readonly _loanBannerService = inject(LoanBannerService);

  readonly borrowerId = input<number>(undefined);

  readonly table = viewChild<Table>('pt');

  currentEmployersGridColumns = [];
  currentOtherIncomeGridColumns = [];
  employmentModel: EmploymentModel[];
  borrowerEmployers$ = of({});
  otherIncome$ = of([]);
  netRentalIncome$ = of(0);
  totalIncome$ = of(0);
  accTabtitle: string = null;
  editingId: any = null;
  editable = false;
  isNewIncome = false;
  incomes: IncomeModel[];
  incomeTypes = CmgUiUtilities.sortLookup(employmentIncomeTypes, 'value');
  otherIncomeTypes = CmgUiUtilities.sortLookup([...employmentIncomeTypes, ...nonEmploymentIncomeTypes], 'value');
  isFullyDisabled$ = this._storeQuery.statusRequiresDisabling$();

  displayPosition: boolean;
  position: string;
  id: number; // employerId
  borrower: BorrowerModel;
  EmploymentStatus = EmploymentStatus;

  ngOnInit(): void {
    this.currentEmployersGridColumns = this._buildEmployersGridColumns();
    this._storeQuery
      .getBorrowersBySelectedApplicationId()
      .pipe(
        take(1),
        map((borrowers) => borrowers.find((borrower) => borrower.borrowerId === this.borrowerId())),
      )
      .subscribe((borrower) => {
        this.borrower = borrower;
        this.accTabtitle = this.borrower.firstName + ' ' + this.borrower.lastName + (this.borrower.isPrimaryBorrower ? '' : ' (Additional Borrower)');
      });
    this.currentOtherIncomeGridColumns = this._buildOtherIncomeGridColumns();
    this.borrowerEmployers$ = this._empIncExpQuery.getBorrowerEmploymentIncome$(this.borrowerId(), this.borrower.isPrimaryBorrower);
    this.otherIncome$ = this._empIncExpQuery.selectActiveIncomeList$(this.borrowerId());
    this.netRentalIncome$ = this._assetsQuery.getNetRentalIncome$(this.borrowerId());
    this.totalIncome$ = this._empIncExpQuery.getTotalIncomeByBorrower$(this.borrowerId());
  }

  getEmploymentIncome(rowData) {
    return this._empIncExpQuery.getEmploymentMonthlyIncome$(rowData.employmentId, this.borrowerId());
  }

  showPositionDialog(position: string, employmentId: number) {
    this.id = employmentId;
    this.position = position;
    this.displayPosition = true;
  }

  closeModal() {
    this.id = undefined;
  }

  formatPhone(val) {
    val = val.length === 10 ? val.replace(/^(\d{0,3})(\d{0,3})(\d{0,4})/, '($1) $2-$3') : val;
    return val;
  }

  deleteBorrowerEmployer(data) {
    const dialogData = {
      message: 'Are you sure you want to delete this?',
      header: 'Confirm',
      icon: 'pi pi-trash',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
    };

    if (data.employmentType === EmploymentStatus.Primary) {
      dialogData.message =
        'Deleting a primary employer requires saving your loan file. All unsaved changes will be saved along with this deletion.<p>Do you want to <strong>delete and save</strong>?';
      dialogData.acceptLabel = 'Yes, Delete & Save';
      dialogData.rejectLabel = 'No, Cancel';
    }

    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      draggable: true,
      keepInViewport: true,
      data: dialogData,
    });

    dialogRef.onClose.subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        if (data.employmentType === EmploymentStatus.Primary) {
          this._empIncExpStore.deleteBorrowerPrimaryEmployment(
            this._appQuery.getActiveId() as number,
            this.borrower.isPrimaryBorrower,
            data.employmentId,
          );
          this._loanBannerService.updateSaveButtonClicked(true);
        } else {
          this._empIncExpStore.deleteEmployment(this._appQuery.getActiveId() as number, this.borrower.isPrimaryBorrower, data.employmentId);
        }
      }
    });
  }

  getIncomeNamebyId(id: number) {
    if (id >= 0) {
      return EmploymentStatus.Former === id ? 'Previous' : EmploymentStatus.Primary === id ? 'Current' : 'Secondary Current';
    }
  }

  getTotalOtherIncome() {
    return this.otherIncome$?.pipe(
      map(
        (incomes) =>
          incomes?.map((inc) => (!inc.incomeType || !(inc.amount >= 0) || inc.isDeleted === true ? 0 : inc.amount)).reduce((a, b) => a + b, 0),
        0,
      ),
    );
  }

  addEmployer() {
    this.id = 0;
    this.position = 'right';
    this.displayPosition = true;
  }

  onRowEditSave(appId: number, row) {
    const amount = nonNullish(row.amount) ? +row.amount : null;
    const currentRow: IncomeModel = {
      ...row,
      amount,
      incomeRate: amount,
      incomeFrequencyType: IncomeFrequencyType.Monthly,
    };
    this._empIncExpStore.updateIncomeModel(appId, currentRow.incomeId, currentRow);
  }

  onNewClicked() {
    const newincome = IncomeModel.createEmpty(this.borrowerId());
    newincome.incomeFrequencyType = IncomeFrequencyType.Monthly;
    const addRow = {
      ...newincome,
      applicationId: this._appQuery.getActiveId() as number,
    };
    this._empIncExpStore.updateIncome(
      this._appQuery.getActiveId() as number,
      newincome.incomeId,
      newincome.incomeType,
      newincome.amount,
      newincome.amount, // incomeRate should be the same as amount
      this.borrowerId(),
      newincome.employmentId,
    );
    this.table().initRowEdit(addRow);
    this.isNewIncome = true;
  }

  deleteOtherIncome(data) {
    const dialogRef = this._dialogService.open(ConfirmationModalComponent, {
      closable: true,
      width: '512px',
      draggable: true,
      keepInViewport: true,
      data: {
        message: 'Are you sure you want to delete this?',
        header: 'Confirm',
        icon: 'pi pi-trash',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
      },
    });

    dialogRef.onClose.subscribe((value: ConfirmationModalCloseType) => {
      if (value === ConfirmationModalCloseType.ClosedViaAcceptButton) {
        this._empIncExpStore.deleteIncome(this._appQuery.getActiveId() as number, data.incomeId);
      }
    });
  }

  onRowEditInit(row: IncomeModel) {
    this.editingId = row.incomeId;
    this.editable = !this.editable;
    this.isNewIncome = false;
  }

  onRowEditCancel(rowData) {
    if (this.isNewIncome) {
      this._empIncExpStore.deleteIncome(this._appQuery.getActiveId() as number, rowData.incomeId);
      this.isNewIncome = false;
    } else {
      this.otherIncome$.subscribe((val) => (this.table().value = val));
    }
  }

  private _buildEmployersGridColumns(): any[] {
    return [
      { field: 'employerName', header: 'Employer', width: '17%' },
      { field: 'employmentType', header: 'Type', width: '13%' },
      { field: 'businessPhone', header: 'Phone', width: '13%', class: 'actions-column' },
      { field: 'street', header: 'Address', width: '23%', class: 'actions-column' },
      { field: 'dateFrom', header: 'Dates', width: '12%', class: 'actions-column' },
      { field: 'monthlyIncome', header: 'Monthly Income', width: '12%', class: 'actions-column' },
      { field: 'additionalInfo', header: 'Additional Info', width: '10%', class: 'actions-column' },
    ];
  }

  private _buildOtherIncomeGridColumns(): any[] {
    return [
      { field: 'incomeType', header: 'Type', width: '50%' },
      { field: 'amount', header: 'Monthly Income', width: '45%' },
      { field: 'actions', header: 'Actions', width: '5%', class: 'actions-column' },
    ];
  }
}
